import {
  Children,
  cloneElement,
  ComponentType,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { EntityPlaylistDialog } from '@backstage-community/plugin-playlist';
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd';
import { EntityLayout } from '@backstage/plugin-catalog';
import { BayerGradientHeader } from '../../theme/BayerGradientHeader';
import {
  useEntity,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useTabPreferences } from './hooks/useTabPreferences';
import { useEntityTabConfig } from './hooks/useEntityTabConfig';
import { EntityOwnerTabSettings } from './EntityOwnerTabSettings';
import { useIsEntityTabConfigOwner } from './useIsEntityTabConfigOwner';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import {
  OverflowTabRoute,
  toOverflowTabRoute,
} from './entityTabVisibility';
import { CustomFeedbackRatingsTable } from '../entity-feedback/CustomFeedbackRatingsTable';

export const StarredRatingsRoute = () => {
  const { entity } = useEntity();

  return (
    <CustomFeedbackRatingsTable
      ownerRef={stringifyEntityRef(entity)}
      title="Entity Ratings"
    />
  );
};

type EntityRouteElementProps = {
  title?: string;
  path?: string;
  if?: (entity: Entity) => boolean;
  children?: ReactNode;
};

const PrivateEntityTabNoAccess = ({
  entity,
  ownerTabConfigLoadError,
}: {
  entity: Entity;
  ownerTabConfigLoadError?: string;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const ownerRef =
    typeof entity.spec?.owner === 'string' ? entity.spec.owner : undefined;
  const ownerName = ownerRef?.split('/').pop() ?? ownerRef;
  const ownerDisplayName = ownerName || 'the repository owner';
  const isGroupOwner = ownerRef?.startsWith('group:');
  const ownerRoleLabel = isGroupOwner ? 'Team Owner' : 'Owner';
  const sourceLocation =
    entity.metadata.annotations?.['backstage.io/source-location'] ?? '';
  // Strip optional "url:" prefix used by Backstage's source-location annotation
  const repoUrlMatch = sourceLocation.replace(/^url:/, '').match(
    /https:\/\/github\.com\/([^/]+)\/([^/]+)/,
  );
  const githubOrg = repoUrlMatch?.[1];
  const repoName = repoUrlMatch?.[2];

  const githubTeamUrl =
    githubOrg && ownerName && isGroupOwner
      ? `https://github.com/orgs/${encodeURIComponent(githubOrg)}/teams/${encodeURIComponent(ownerName)}`
      : undefined;

  const handleCopyInfo = () => {
    const repository = `${githubOrg || 'unknown-org'}/${repoName || 'unknown-repo'}`;
    const info = `Component: ${entity.metadata.name}\nOwner: ${ownerName || 'Unknown'}\nRepository: ${repository}`;

    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      setCopied(false);
      return;
    }

    void navigator.clipboard
      .writeText(info)
      .then(() => {
        setCopied(true);
        if (copyResetTimeoutRef.current) {
          clearTimeout(copyResetTimeoutRef.current);
        }
        copyResetTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copyResetTimeoutRef.current = undefined;
        }, 2000);
      })
      .catch(() => {
        setCopied(false);
      });
  };

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box padding={4}>
      <Box marginBottom={3}>
        <Typography variant="h4" gutterBottom style={{ fontWeight: 500 }}>
          Limited View
        </Typography>
        <Typography variant="body2" color="textSecondary">
          This component belongs to a private repository. In Backstage,
          non-owners only see tabs explicitly revealed by the component owners.
        </Typography>
        {ownerTabConfigLoadError && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            We could not load tab visibility settings right now. Showing a
            limited view to protect private content. Try refreshing the page.
          </Typography>
        )}
      </Box>

      <Box marginBottom={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box
              padding={2}
              border="1px solid #e0e0e0"
              borderRadius={1}
              bgcolor="background.paper"
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }} gutterBottom>
                Repository Owner
              </Typography>
              <Typography variant="body2" paragraph>
                {ownerName || 'No owner specified'}
              </Typography>
              {githubOrg && (
                <Typography variant="caption" color="textSecondary">
                  GitHub Org: <strong>{githubOrg}</strong>
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              padding={2}
              border="1px solid #e0e0e0"
              borderRadius={1}
              bgcolor="background.paper"
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600 }} gutterBottom>
                Component Details
              </Typography>
              <Typography variant="body2" paragraph>
                {entity.metadata.name}
              </Typography>
              {repoName && (
                <Typography variant="caption" color="textSecondary">
                  Repository: <strong>{repoName}</strong>
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box marginBottom={3}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }} gutterBottom>
          Need Additional Tabs?
        </Typography>
        <List dense style={{ paddingLeft: 0 }}>
          <ListItem disableGutters style={{ paddingLeft: 0, marginBottom: 8 }}>
            <Typography variant="body2">
              {isGroupOwner ? (
                <>
                  <strong>1. Join the GitHub team:</strong> Ask a team admin to add
                  you to the <strong>{ownerDisplayName}</strong> team
                </>
              ) : (
                <>
                  <strong>1. Contact the repository owner:</strong> Reach out to{' '}
                  <strong>{ownerDisplayName}</strong> to request access
                </>
              )}
            </Typography>
          </ListItem>
          <ListItem disableGutters style={{ paddingLeft: 0, marginBottom: 8 }}>
            <Typography variant="body2">
              <strong>2. Become a collaborator:</strong> Request to be added as
              a collaborator on this repository in GitHub
            </Typography>
          </ListItem>
          <ListItem disableGutters style={{ paddingLeft: 0 }}>
            <Typography variant="body2">
              <strong>3. Refresh the page:</strong> Once access is granted,
              refresh this page to see all available content
            </Typography>
          </ListItem>
        </List>
      </Box>

      <Box display="flex" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setDialogOpen(true)}
        >
          View Access Options
        </Button>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
        <Button
          variant="outlined"
          onClick={handleCopyInfo}
          title="Copy owner and component details"
        >
          {copied ? 'Copied!' : 'Copy Info'}
        </Button>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 600 }}>Request Broader Access</DialogTitle>
        <DialogContent>
          <Box marginTop={1}>
            <Typography variant="body2" paragraph>
              You can view this entity, but some tabs are intentionally restricted
              to owners in Backstage. Contact the repository owner or team admin
              if you need broader visibility.
            </Typography>

            {ownerName && (
              <Box marginBottom={2} padding={1.5} border="1px solid rgba(128,128,128,0.3)" borderRadius={1}>
                <Typography variant="caption" color="textSecondary">
                  {ownerRoleLabel}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {ownerName}
                </Typography>
              </Box>
            )}

            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              Access Paths:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {isGroupOwner ? 'Join the team on GitHub' : 'Contact the repository owner'}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption">
                      {isGroupOwner
                        ? 'Contact a team admin to add you'
                        : 'Ask the owner to grant the access you need'}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<Typography variant="body2">Request collaborator access</Typography>}
                  secondary={<Typography variant="caption">Ask to be added to this repository</Typography>}
                />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {githubTeamUrl && (
            <Button
              color="primary"
              variant="contained"
              href={githubTeamUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Go to GitHub Team
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Small dialog to navigate to overflow (preference-hidden) tabs */
const OverflowTabsDialog = ({
  open,
  onClose,
  routes,
  entityBasePath,
  onNavigate,
  ownerHiddenTabs,
  preferredTabs,
}: {
  open: boolean;
  onClose: () => void;
  routes: OverflowTabRoute[];
  entityBasePath: string;
  onNavigate: (route: OverflowTabRoute, fullPath: string) => void;
  ownerHiddenTabs: string[];
  preferredTabs: string[];
}) => {
  const navigate = useNavigate();
  const canOpenCount = routes.filter(r => r.available).length;
  const needsSetupCount = routes.length - canOpenCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <MoreHorizIcon color="action" fontSize="small" />
          More Tabs
        </Box>
      </DialogTitle>
      <DialogContent>
        {ownerHiddenTabs.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            style={{
              gap: 8,
              marginBottom: 12,
              padding: 8,
              backgroundColor: '#fff3cd',
              borderRadius: 4,
            }}
          >
            <VisibilityOffIcon fontSize="small" color="action" />
            <Typography variant="caption">
              {ownerHiddenTabs.length} tab{ownerHiddenTabs.length !== 1 ? 's are' : ' is'}{' '}
              hidden by the owner.
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Tabs outside your catalog tab preferences. Only tabs marked{' '}
          <strong>Can add</strong> can be opened for this entity. Tabs marked{' '}
          <strong>Needs setup</strong> require annotations in{' '}
          <code>catalog-info.yaml</code>.
        </Typography>
        {(canOpenCount > 0 || needsSetupCount > 0) && (
          <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
            {canOpenCount > 0 && (
              <Chip
                size="small"
                label={`${canOpenCount} can add`}
                color="primary"
                variant="outlined"
              />
            )}
            {needsSetupCount > 0 && (
              <Chip
                size="small"
                label={`${needsSetupCount} needs setup`}
                variant="outlined"
              />
            )}
          </Box>
        )}
        <List disablePadding>
          {routes.map((route) => {
            const tabPath =
              route.path === '/'
                ? ''
                : route.path.startsWith('/')
                  ? route.path
                  : `/${route.path}`;
            const fullPath = `${entityBasePath}${tabPath}`;
            const primaryLabel = (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <span>{route.title}</span>
                <Chip
                  size="small"
                  label={route.available ? 'Can add' : 'Needs setup'}
                  color={route.available ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
            );

            if (route.available) {
              return (
                <ListItem
                  key={route.title}
                  button
                  onClick={() => onNavigate(route, fullPath)}
                >
                  <ListItemText primary={primaryLabel} />
                </ListItem>
              );
            }

            return (
              <Tooltip
                key={route.title}
                title={route.unavailableReason ?? 'Not available for this entity'}
                placement="left"
              >
                <span>
                  <ListItem disabled>
                    <ListItemText
                      primary={primaryLabel}
                      secondary={route.unavailableReason}
                    />
                  </ListItem>
                </span>
              </Tooltip>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        {preferredTabs.length > 0 && (
          <Button
            onClick={() => {
              onClose();
              navigate('/settings/my-profile');
            }}
            color="primary"
            size="small"
          >
            Manage preferences
          </Button>
        )}
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EntityLayoutWrapper = (props: {
  children?: ReactNode;
  LayoutComponent?: ComponentType<any>;
  layoutProps?: Record<string, unknown>;
  contextMenuItems?: ReactNode[];
  includePlaylistMenuItem?: boolean;
  includeOwnerTabVisibilityMenuItem?: boolean;
}) => {
  const {
    children,
    LayoutComponent = EntityLayout as ComponentType<any>,
    layoutProps,
    contextMenuItems,
    includePlaylistMenuItem = true,
    includeOwnerTabVisibilityMenuItem = true,
  } = props;
  const [playlistDialogOpen, setPlaylistDialogOpen] = useState(false);
  const [ownerTabSettingsOpen, setOwnerTabSettingsOpen] = useState(false);
  const [moreTabsOpen, setMoreTabsOpen] = useState(false);
  const Layout = LayoutComponent as ComponentType<any>;

  // Entity context – provided by CatalogEntityPage above us in the tree.
  // Use the async variant so we don't throw while the entity is still
  // loading (e.g. right after navigating to an owner's entity page).
  const { entity } = useAsyncEntity();
  const isOwner = useIsEntityTabConfigOwner(entity);

  // User's preferred tabs & entity owner config
  const { preferredTabs } = useTabPreferences();
  const {
    ownerHiddenTabs,
    ownerVisibleTabs = [],
    loadError: ownerTabConfigLoadError,
    loading: ownerHiddenTabsLoading,
  } =
    useEntityTabConfig(entity);
  const repoVisibility =
    entity?.metadata.annotations?.['github.com/repo-visibility']?.toLowerCase() ??
    '';
  const isPrivateEntity = repoVisibility === 'private';
  const showPrivateNoAccessForNonOwner =
    isPrivateEntity &&
    !isOwner &&
    (ownerHiddenTabsLoading ||
      ownerTabConfigLoadError ||
      ownerVisibleTabs.length === 0);

  // Current path – used to keep the active tab visible even if hidden by preference
  const location = useLocation();
  const navigate = useNavigate();

  // ── Build the filtered children list ────────────────────────────────────
  const { visibleChildren, overflowRoutes, availableTabs } = useMemo(() => {
    const allChildren = Children.toArray(children);

    // Collect all titled routes for the "available tabs" list
    const allTabs: string[] = allChildren
      .filter(
        (child): child is ReactElement<EntityRouteElementProps> =>
          isValidElement<EntityRouteElementProps>(child),
      )
      .map(child => child.props.title ?? '')
      .filter(Boolean);

    // Entity still loading — skip tab filtering until we have a resolved entity
    if (!entity) {
      return {
        visibleChildren: children,
        overflowRoutes: [] as OverflowTabRoute[],
        availableTabs: allTabs,
      };
    }

    if (isOwner) {
      // Owners always see every tab
      return {
        visibleChildren: children,
        overflowRoutes: [] as OverflowTabRoute[],
        availableTabs: allTabs,
      };
    }

    // Determine the active tab path relative to the entity base URL
    // e.g. for /catalog/default/component/my-service/ci-cd → 'ci-cd'
    //      for /catalog/default/component/my-service          → '' (overview)
    const ns = entity.metadata.namespace ?? 'default';
    const kind = entity.kind.toLowerCase();
    const entityName = encodeURIComponent(entity.metadata.name);
    const entityBase = `/catalog/${ns}/${kind}/${entityName}`;
    const currentTabPath = location.pathname.startsWith(entityBase)
      ? location.pathname.slice(entityBase.length).replace(/^\//, '')
      : '';
    const overflow: OverflowTabRoute[] = [];

    // For non-owners we clone each Route and override its `if` prop to
    // `() => false` when the tab should be hidden.  EntityLayout reads the
    // `if` prop from its children via useElementFilter, so this is the only
    // reliable way to suppress a tab without removing it from the tree.
    // Private entities: only Overview auto-appears with any reveal; Docs must
    // be explicitly listed in ownerVisibleTabs (matches TechDocs API grant).
    let requestTabInserted = false;
    const modifiedChildren = allChildren.map(child => {
      if (!isValidElement<EntityRouteElementProps>(child)) return child;

      const childProps = child.props;
      const title = childProps.title ?? '';
      const path = childProps.path ?? '';
      const routeIf = childProps.if;
      const routeSegment = path.replace(/^\//, '');
      const isActive =
        routeSegment === currentTabPath ||
        (path === '/' && currentTabPath === '');

      if (!title) return child;

      if (showPrivateNoAccessForNonOwner) {
        // Keep one visible "Request Access" tab. Do not mount hidden deep-link
        // placeholders — empty/display:none tabs break the MUI selected indicator.
        // Unmatched deep-link URLs fall back to this first tab's content.
        const isFirstTab = !requestTabInserted;
        requestTabInserted = true;

        if (isFirstTab) {
          return cloneElement(child, {
            title: 'Request Access',
            if: () => true,
            children: (
              <PrivateEntityTabNoAccess
                entity={entity}
                ownerTabConfigLoadError={ownerTabConfigLoadError}
              />
            ),
          });
        }

        return cloneElement(child, {
          if: () => false,
        });
      }

      // ── Private entities: reveal-only model for non-owners ─────────────
      // All tabs (including Overview) must be explicitly listed in
      // ownerVisibleTabs. Owners who leave Overview unchecked will suppress it
      // from non-owners just like any other tab.
      if (!ownerHiddenTabsLoading && !ownerTabConfigLoadError && isPrivateEntity) {
        if (!ownerVisibleTabs.includes(title)) {
          return cloneElement(child, {
            if: () => false,
          });
        }
      }

      // ── Owner has hidden this tab for non-owners ──────────────────────
      // Skip while loading to preserve navigation stability; once the config
      // resolves the tab will be hidden on the next render if needed.
      if (
        !isPrivateEntity &&
        !ownerHiddenTabsLoading &&
        ownerHiddenTabs.includes(title)
      ) {
        return cloneElement(child, {
          if: () => false,
        });
      }

      // ── User preference filtering (always applied) ────────────────────
      if (preferredTabs.length > 0 && !preferredTabs.includes(title)) {
        // Keep the active tab visible even when not in the preferred list
        if (!isActive) {
          overflow.push(toOverflowTabRoute(entity, title, path, routeIf));
          return cloneElement(child, {
            if: () => false,
          });
        }
      }

      return child;
    });

    return {
      visibleChildren: modifiedChildren,
      overflowRoutes: overflow,
      availableTabs: allTabs,
    };
  }, [
    children,
    isOwner,
    ownerHiddenTabs,
    ownerVisibleTabs,
    ownerTabConfigLoadError,
    ownerHiddenTabsLoading,
    isPrivateEntity,
    showPrivateNoAccessForNonOwner,
    preferredTabs,
    location.pathname,
    entity,
  ]);

  // ── Extra context-menu items ─────────────────────────────────────────────
  const extraMenuItems = useMemo(() => {
    const items: Array<{
      title: string;
      Icon: ComponentType<any>;
      onClick: () => void;
    }> = [];

    if (includePlaylistMenuItem) {
      items.push({
        title: 'Add to playlist',
        Icon: PlaylistAddIcon,
        onClick: () => setPlaylistDialogOpen(true),
      });
    }

    if (isOwner && includeOwnerTabVisibilityMenuItem) {
      items.push({
        title: 'Manage tab visibility',
        Icon: VisibilityOffIcon,
        onClick: () => setOwnerTabSettingsOpen(true),
      });
    }

    return items;
  }, [
    includeOwnerTabVisibilityMenuItem,
    includePlaylistMenuItem,
    isOwner,
  ]);

  // Build the entity base path for overflow navigation
  const entityBasePath = useMemo(() => {
    if (!entity) return '';
    const ns = entity.metadata.namespace ?? 'default';
    const kind = entity.kind.toLowerCase();
    const name = encodeURIComponent(entity.metadata.name);
    return `/catalog/${ns}/${kind}/${name}`;
  }, [entity]);

  const moreTabsPath = useMemo(() => {
    if (!entityBasePath) return '';
    return `${entityBasePath}/more-tabs`;
  }, [entityBasePath]);

  const lastRealTabPathRef = useRef<string>('');

  useEffect(() => {
    if (!entityBasePath || !location.pathname.startsWith(entityBasePath)) return;
    if (location.pathname !== moreTabsPath) {
      lastRealTabPathRef.current = `${location.pathname}${location.search}${location.hash}`;
    }
  }, [entityBasePath, location.pathname, location.search, location.hash, moreTabsPath]);

  useEffect(() => {
    if (!moreTabsPath || location.pathname !== moreTabsPath) return;

    if (overflowRoutes.length === 0) {
      navigate(entityBasePath, { replace: true });
      return;
    }

    setMoreTabsOpen(true);
    navigate(lastRealTabPathRef.current || entityBasePath, { replace: true });
  }, [
    entityBasePath,
    location.pathname,
    moreTabsPath,
    navigate,
    overflowRoutes.length,
  ]);

  const trailingChildren = useMemo(() => {
    if (overflowRoutes.length === 0) return visibleChildren;

    return [
      ...Children.toArray(visibleChildren),
      <EntityLayout.Route
        key="more-tabs-trailing-route"
        path="/more-tabs"
        title={`More tabs (${overflowRoutes.length} hidden)`}
      >
        <div />
      </EntityLayout.Route>,
    ];
  }, [overflowRoutes.length, visibleChildren]);

  return (
    <>
      <BayerGradientHeader>
        <Layout
          UNSTABLE_extraContextMenuItems={extraMenuItems}
          contextMenuItems={contextMenuItems}
          {...layoutProps}
        >
          {trailingChildren}
        </Layout>
      </BayerGradientHeader>

      {/* Overflow tabs dialog */}
      {moreTabsOpen && (
        <OverflowTabsDialog
          open={moreTabsOpen}
          onClose={() => setMoreTabsOpen(false)}
          routes={overflowRoutes}
          entityBasePath={entityBasePath}
          ownerHiddenTabs={ownerHiddenTabs}
          preferredTabs={preferredTabs}
          onNavigate={(route, path) => {
            if (!route.available) return;
            setMoreTabsOpen(false);
            navigate(path);
          }}
        />
      )}

      {/* Owner: manage tab visibility for non-owners */}
      {isOwner && includeOwnerTabVisibilityMenuItem && entity && ownerTabSettingsOpen && (
        <EntityOwnerTabSettings
          open={ownerTabSettingsOpen}
          onClose={() => setOwnerTabSettingsOpen(false)}
          entity={entity}
          availableTabs={availableTabs}
        />
      )}

      {includePlaylistMenuItem && (
        <EntityPlaylistDialog
          open={playlistDialogOpen}
          onClose={() => setPlaylistDialogOpen(false)}
        />
      )}
    </>
  );
};

